import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { FC, useEffect, useState } from 'react'

import { useRequestById } from '@/api/models/requests'
import { REQUESTS_LIST_RQ_KEY } from '@/api/models/requests/hooks/useRequestsList'
import { RequestTypeAlias } from '@/api/models/requests/lib/requests.types'
import { ServerValidationError } from '@/api/shared/types'
import { PASS_CANCELLED, REQUEST_CANCELLED } from '@/app/constants/constants'
import { cn } from '@/app/utils'
import { AvailablePasses } from '@/components/entities/passes/ui/AvailablePasses'
import { PassFormWrapper } from '@/components/entities/passes/ui/PassForms/PassFormWrapper'
import { PassFormTitle } from '@/components/entities/passes/ui/PassFormTitle'
import { PageType } from '@/components/entities/requests/model/types'
import ContentSkeleton from '@/components/entities/requests/ui/RequestDetails/Content/ContentSkeleton'
import { RequestDetails } from '@/components/entities/requests/ui/RequestDetails/RequestDetails'
import { RequestForm } from '@/components/entities/requests/ui/RequestForm/RequestForm'
import GoBackButton from '@/components/shared/button/ui/GoBackButton'
import { AppDrawer } from '@/components/shared/drawer'
import { ButtonSkeleton } from '@/components/shared/skeleton'
import { AppToast } from '@/components/shared/toast'
import { ErrorDrawerContent } from '@/components/widgets/request/ErrorDrawerContent'
import { ErrorDrawerTitle } from '@/components/widgets/request/ErrorDrawerTitle'
import { useRequestNavigation } from '@/components/widgets/request/hooks/useRequestNavigation'
import { useRequestParams } from '@/components/widgets/request/hooks/useRequestParams'
import { RequestFormTitle } from '@/components/widgets/request/RequestFormTitle'
import { RequestSuccess } from '@/components/widgets/request/RequestSuccess'
import RequestTitleDetails from '@/components/widgets/request/RequestTitleDetails'
import { RequestTitleSuccess } from '@/components/widgets/request/RequestTitleSuccess'
import { StepConfirmContent } from '@/components/widgets/request/StepConfirmContent'
import { StepConfirmTitle } from '@/components/widgets/request/StepConfirmTitle'
import { sleep } from '@/utils'
import { isForbiddenError } from '@/utils/isForbiddenError'

import { RequestBookingSuccess } from './RequestBookingSuccess'
import { RequestCancelled } from './RequestCancelled'
import { RequestEditingForm } from './RequestEditingForm'
import { RequestPaymentForm } from './RequestPaymentForm'

type Props = {
  pageType: PageType
}

const RequestDrawer: FC<Props> = ({ pageType }) => {
  const {
    typeAliasFromUrl,
    isValidTypeAlias,
    isValidPasses,
    requestId,
    bookingId,
    paymentInvoiceId,
    paymentPersonalAccountId,
    hasRepeatParam,
    isRentSummaryStep,
    isRequestEditing,
    editingAutoId,
    navigateBackFromRentSummary,
    navigateBackFromEditing
  } = useRequestParams(pageType)

  const {
    navigateToCloseDetails,
    navigateToAvailablePasses,
    navigateToPaymentFromDetails,
    navigateFromPayment,
    navigateToBookingUnpaid,
    navigateBackFromBookingUnpaid,
    shouldShowPayment,
    shouldShowBookingUnpaid
  } = useRequestNavigation()

  const {
    data: request,
    error,
    isError,
    isLoading,
    refetch: refetchRequest
  } = useRequestById({
    id: requestId ?? ''
  })

  const queryClient = useQueryClient()

  const [isOpenDrawer, setIsOpenDrawer] = useState(
    isValidTypeAlias || isValidPasses
  )

  const [isFormSuccess, setIsFormSuccess] = useState(false)

  /**
   * isRequestCancelled - флаг отображения экрана об успешной отмене
   * пропуска/обращения из детальной карточки
   */
  const [isRequestCancelled, setIsRequestCancelled] = useState(false)
  const [formError, setFormError] =
    useState<AxiosError<ServerValidationError> | null>(null)

  /**
   * isFormDirty - флаг, указывающий были ли внесены изменения в форму создания обращения
   * Используется для отслеживания несохраненных данных и показа предупреждений
   */
  const [isFormDirty, setIsFormDirty] = useState(false)

  /**
   * shouldShowExitConfirm - флаг отображения окна подтверждения при попытке вернуться назад
   * Показывается, когда форма содержит несохраненные изменения и пользователь нажимает кнопку "Назад"
   */
  const [shouldShowExitConfirm, setShouldShowExitConfirm] = useState(false)

  /**
   * shouldShowCloseConfirm - флаг отображения окна подтверждения при попытке закрыть drawer
   * Показывается, когда форма содержит несохраненные изменения и пользователь нажимает кнопку "Закрыть"
   */
  const [shouldShowCloseConfirm, setShouldShowCloseConfirm] = useState(false)

  /**
   * isEditingSuccessVisible - флаг показа тоста об успешном изменении данных о ТС
   */
  const [isEditingSuccessVisible, setIsEditingSuccessVisible] = useState(false)

  const shouldOpenDrawer =
    Boolean(requestId) ||
    Boolean(bookingId && shouldShowPayment) ||
    isValidTypeAlias ||
    isValidPasses

  useEffect(() => {
    setIsOpenDrawer(shouldOpenDrawer)

    if (!shouldOpenDrawer) {
      setShouldShowExitConfirm(false)
      setShouldShowCloseConfirm(false)
      setIsFormSuccess(false)
      setIsRequestCancelled(false)
      setFormError(null)
      setIsFormDirty(false)
      setIsEditingSuccessVisible(false)
    }
  }, [shouldOpenDrawer])

  const clearQueryParams = async () => {
    setIsOpenDrawer(false)
    await sleep(300)
    setShouldShowExitConfirm(false)
    setShouldShowCloseConfirm(false)
    setIsFormSuccess(false)
    setIsRequestCancelled(false)
    setFormError(null)
    await navigateToCloseDetails()
  }

  const handleCloseBookingUnpaid = async () => {
    queryClient.invalidateQueries({ queryKey: [REQUESTS_LIST_RQ_KEY] })
    await clearQueryParams()
  }

  const handlePassesNavigation = () => {
    if (pageType === PageType.Passes && isValidTypeAlias) {
      setShouldShowExitConfirm(false)
      setShouldShowCloseConfirm(false)
      setIsFormDirty(false)
      setFormError(null)
      navigateToAvailablePasses()
      return true
    }
    return false
  }

  const handleGoBack = async () => {
    if (isFormDirty && !formError) {
      setShouldShowExitConfirm(true)
      return
    }

    if (handlePassesNavigation()) {
      return
    }
    await clearQueryParams()
  }

  const handleCloseDrawer = async () => {
    if (shouldShowBookingUnpaid) {
      await handleCloseBookingUnpaid()
      return
    }

    /**
     * Бронирование пропуска на аренду уже создано на этом шаге,
     * поэтому вместо предупреждения о несохраненных данных
     * показываем шторку "Пропуск создан"
     */
    if (shouldShowPayment && Boolean(bookingId)) {
      navigateToBookingUnpaid()
      return
    }

    if (shouldShowEditingForm) {
      navigateBackFromEditing()
      return
    }

    if (
      isFormDirty &&
      !shouldShowCloseConfirm &&
      !shouldShowExitConfirm &&
      !formError
    ) {
      setShouldShowCloseConfirm(true)
      return
    }

    await clearQueryParams()
  }

  /**
   * shouldShowRequestForm - флаг отображения формы создания обращения
   */
  const shouldShowRequestForm =
    pageType === PageType.Requests &&
    !formError &&
    !isError &&
    (hasRepeatParam ||
      (typeAliasFromUrl === RequestTypeAlias.CommonRequest && !request?.id))

  /**
   * shouldShowEditingForm - флаг отображения экрана редактирования данных о ТС
   * Редактирование доступно, только если бэк разрешил его флагом canAutoBeChanged
   */
  const shouldShowEditingForm =
    isRequestEditing && Boolean(request?.canAutoBeChanged) && !formError

  // если вручную в адресную строку написать квери для перехода в редактирование, но на бэке флаг выключен,
  // то кидаем на деталку назад
  useEffect(() => {
    if (isRequestEditing && request?.id && !request.canAutoBeChanged) {
      navigateBackFromEditing()
    }
  }, [
    isRequestEditing,
    request?.id,
    request?.canAutoBeChanged,
    navigateBackFromEditing
  ])

  /**
   * shouldShowRequestDetails - флаг отображения детальной карточки обращения
   */
  const shouldShowRequestDetails =
    request?.id &&
    !hasRepeatParam &&
    !formError &&
    !isFormSuccess &&
    !isRequestCancelled &&
    !shouldShowPayment &&
    !shouldShowEditingForm

  /**
   * shouldShowRequestDetailsLoading - флаг отображения скелетона детальной карточки
   * на время загрузки обращения/пропуска по id
   */
  const shouldShowRequestDetailsLoading =
    isLoading &&
    Boolean(requestId) &&
    !hasRepeatParam &&
    !formError &&
    !shouldShowPayment

  /**
   * shouldShowAvailablePasses - флаг отображения списка доступных пропусков
   */
  const shouldShowAvailablePasses =
    pageType === PageType.Passes &&
    !request?.id &&
    isValidPasses &&
    !isValidTypeAlias &&
    !formError

  /**
   * shouldShowPassForm - флаг отображения формы создания пропуска
   */
  const shouldShowPassForm =
    pageType === PageType.Passes &&
    !formError &&
    (isValidTypeAlias || hasRepeatParam)

  /**
   * shouldShowForbiddenError / shouldShowCommonError - флаги отображения ошибок,
   * экран отмены их перекрывает: после удаления запрос по id больше не доступен
   */
  const shouldShowForbiddenError =
    isForbiddenError(error) && !isRequestCancelled

  const shouldShowCommonError =
    Boolean(isError || formError) &&
    !isForbiddenError(error) &&
    !isRequestCancelled

  const handlePayClick = () => {
    navigateToPaymentFromDetails()
  }

  const handleEditingSuccess = () => {
    setIsEditingSuccessVisible(true)
    navigateBackFromEditing()
  }

  const shouldAddPaddingBottom =
    shouldShowPassForm &&
    typeAliasFromUrl === RequestTypeAlias.RentPassAuto &&
    !shouldShowExitConfirm &&
    !shouldShowCloseConfirm

  return (
    <AppDrawer
      contentWrapperClassName={cn(
        shouldShowPayment &&
          !shouldShowBookingUnpaid &&
          !shouldShowExitConfirm &&
          !shouldShowCloseConfirm
          ? 'px-0 lg:px-0'
          : '',
        shouldAddPaddingBottom ? '' : 'pb-24',
        (shouldShowPassForm || shouldShowRequestForm) && 'overflow-hidden'
      )}
      className="w-[790px] !bg-white md:w-[684px]"
      isLoading={shouldShowRequestDetailsLoading}
      title={
        <>
          <div
            className={
              shouldShowExitConfirm || shouldShowCloseConfirm
                ? 'pointer-events-none hidden'
                : ''
            }
          >
            {shouldShowRequestForm && (
              <RequestFormTitle
                hasRepeatParam={hasRepeatParam}
                onBack={handleGoBack}
              />
            )}
            {shouldShowRequestDetailsLoading && (
              <div className="flex flex-col gap-y-8">
                <ButtonSkeleton className="h-[36px] w-[220px] rounded-4 lg:h-[28px] lg:w-[180px] sm:h-[24px] sm:w-[140px]" />
                <ButtonSkeleton className="h-[24px] w-[110px] rounded-100" />
              </div>
            )}
            {shouldShowRequestDetails && (
              <RequestTitleDetails
                request={request}
                pageType={pageType}
                isError={isForbiddenError(error || formError)}
              />
            )}
            {isFormSuccess && (
              <RequestTitleSuccess pageType={pageType} isLoading={isLoading} />
            )}
            {isRequestCancelled && (
              <h5 className="text-36 lg:text-28 sm:text-20">
                {pageType === PageType.Passes
                  ? PASS_CANCELLED
                  : REQUEST_CANCELLED}
              </h5>
            )}
            {shouldShowBookingUnpaid && (
              <div className="flex items-center gap-24">
                <GoBackButton onClick={navigateBackFromBookingUnpaid} />
                <span>Аренда не оплачена</span>
              </div>
            )}
            {shouldShowAvailablePasses && (
              <div className="flex items-center gap-24">
                <GoBackButton onClick={handleGoBack} />
                <span>Заказать пропуск</span>
              </div>
            )}
            {isRentSummaryStep && (
              <div className="flex items-center gap-24">
                <GoBackButton onClick={navigateBackFromRentSummary} />
                <span>Расчёт стоимости</span>
              </div>
            )}
            {shouldShowEditingForm && (
              <div className="flex items-center gap-24">
                <GoBackButton onClick={navigateBackFromEditing} />
                <span>Изменить данные о ТС</span>
              </div>
            )}
            {shouldShowPayment && !shouldShowBookingUnpaid && (
              <span>Оплата счёта</span>
            )}
            {shouldShowPassForm && !isRentSummaryStep && (
              <PassFormTitle
                type={typeAliasFromUrl}
                hasRepeatParam={hasRepeatParam}
                onBack={handleGoBack}
              />
            )}
          </div>
          {shouldShowExitConfirm && <StepConfirmTitle text="Вернуться назад" />}
          {shouldShowCloseConfirm && (
            <StepConfirmTitle
              text={`Закрыть окно создания ${
                pageType === PageType.Passes ? 'пропуска' : 'обращения'
              }?`}
            />
          )}
          {shouldShowForbiddenError && (
            <ErrorDrawerTitle text="В доступе отказано" />
          )}
          {shouldShowCommonError && (
            <ErrorDrawerTitle
              text={isRequestEditing ? 'Изменить данные не удалось' : undefined}
            />
          )}
        </>
      }
      isVisibleCloseButton={!shouldShowExitConfirm && !shouldShowCloseConfirm}
      opened={isOpenDrawer}
      onClose={handleCloseDrawer}
    >
      <div
        className={
          shouldShowExitConfirm || shouldShowCloseConfirm
            ? 'pointer-events-none hidden'
            : '[display:contents]'
        }
      >
        {shouldShowRequestForm && (
          <RequestForm
            pageType={pageType}
            onFormDirty={setIsFormDirty}
            onFormSuccess={setIsFormSuccess}
            onFormError={setFormError}
            request={request}
          />
        )}
        {shouldShowRequestDetailsLoading && (
          <div className="flex min-h-0 flex-1 flex-col">
            <ContentSkeleton />
          </div>
        )}
        {shouldShowRequestDetails && (
          <RequestDetails
            request={request}
            pageType={pageType}
            refetchRequest={refetchRequest}
            onPayClick={handlePayClick}
            onFormError={setFormError}
            onCancelSuccess={() => setIsRequestCancelled(true)}
          />
        )}
        {shouldShowEditingForm && (
          <RequestEditingForm
            request={request}
            editingAutoId={editingAutoId}
            onSuccess={handleEditingSuccess}
            onError={setFormError}
          />
        )}
        {isFormSuccess && (
          <RequestSuccess
            request={request}
            pageType={pageType}
            isLoading={isLoading}
            onFormSuccess={setIsFormSuccess}
          />
        )}
        {isRequestCancelled && (
          <RequestCancelled pageType={pageType} onClose={handleCloseDrawer} />
        )}
        {shouldShowPayment && !shouldShowBookingUnpaid && (
          <RequestPaymentForm
            returnBackUrl={bookingId ? '?after-payment=true' : ''}
            request={request?.id ? request : undefined}
            invoiceId={paymentInvoiceId}
            personalAccountId={paymentPersonalAccountId}
            onClose={navigateFromPayment}
          />
        )}
        {shouldShowBookingUnpaid && (
          <RequestBookingSuccess onNavigateToList={handleCloseBookingUnpaid} />
        )}
        {shouldShowAvailablePasses && <AvailablePasses />}
        {shouldShowPassForm && (
          <PassFormWrapper
            typeAliasFromUrl={typeAliasFromUrl}
            onFormDirty={setIsFormDirty}
            onFormSuccess={setIsFormSuccess}
            onFormError={setFormError}
            pass={request}
          />
        )}

        {shouldShowForbiddenError && (
          <ErrorDrawerContent isForbidden closeDrawer={handleCloseDrawer} />
        )}
        {shouldShowCommonError && (
          <ErrorDrawerContent
            closeDrawer={handleCloseDrawer}
            formError={formError}
            isEditingError={isRequestEditing}
          />
        )}
      </div>

      {shouldShowExitConfirm && (
        <StepConfirmContent
          text="Если вы вернетесь на предыдущий шаг, то заполненные данные не сохранятся"
          closeDrawer={handleCloseDrawer}
          onCancel={() => setShouldShowExitConfirm(false)}
        />
      )}
      {shouldShowCloseConfirm && (
        <StepConfirmContent
          text="Если вы закроете окно, то заполненные данные не сохранятся"
          closeDrawer={handleCloseDrawer}
          onCancel={() => setShouldShowCloseConfirm(false)}
          backButtonText="Закрыть"
        />
      )}

      <AppToast
        opened={isEditingSuccessVisible}
        message="Данные о ТС изменены"
        onClose={() => setIsEditingSuccessVisible(false)}
        icon={<CheckCircleIcon className="h-20 w-20" />}
      />
    </AppDrawer>
  )
}

export { RequestDrawer }
